import { useMemo } from 'react'

/**
 * Derives visible and hidden favorites from the current catalog filtering result.
 *
 * @param {{
 *   favoriteUuids?: Set<string> | string[] | null,
 *   filteredProducts?: Array<{ uuid: string }> | null,
 *   allProducts?: Array<{ uuid: string }> | null,
 * }} params
 * @returns {{
 *   visibleFavorites: Array<object>,
 *   hiddenFavoritesCount: number,
 *   areSomeFavoritesHidden: boolean,
 *   allFavoriteProducts: Array<object>,
 * }}
 */
export function useFavoritesFiltered({
  favoriteUuids,
  filteredProducts,
  allProducts,
}) {
  const favoriteUuidSet = favoriteUuids instanceof Set
    ? favoriteUuids
    : new Set(Array.isArray(favoriteUuids) ? favoriteUuids : [])

  const normalizedFilteredProducts = Array.isArray(filteredProducts) ? filteredProducts : []
  const normalizedAllProducts = Array.isArray(allProducts) ? allProducts : []

  return useMemo(() => {
    const visibleFavorites = normalizedFilteredProducts.filter((product) => favoriteUuidSet.has(product.uuid))
    const allFavoriteProducts = normalizedAllProducts.filter((product) => favoriteUuidSet.has(product.uuid))
    const hiddenFavoritesCount = Math.max(0, favoriteUuidSet.size - visibleFavorites.length)

    return {
      visibleFavorites,
      hiddenFavoritesCount,
      areSomeFavoritesHidden: hiddenFavoritesCount > 0,
      allFavoriteProducts,
    }
  }, [favoriteUuidSet, normalizedAllProducts, normalizedFilteredProducts])
}
